import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, FileDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { usePdfExport } from '@/hooks/usePdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StageStock() {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { exporting, handleExport } = usePdfExport('/export/stage-stock-pdf', 'Stage_Stock');

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock/stage`, {
        
      });
      setStock(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to fetch stock');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  const filtered = useMemo(() => {
    if (!search.trim()) return stock;
    const q = search.toLowerCase();
    return stock.filter(s =>
      s.part_id.toLowerCase().includes(q) ||
      (s.part_name || '').toLowerCase().includes(q)
    );
  }, [stock, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="stage-stock-title">STAGE STOCK</h1>
          <p className="text-sm text-text-muted mt-2">Stage-wise inventory levels</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="export-pdf-button"
            onClick={() => handleExport()}
            disabled={exporting || stock.length === 0}
            className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-medium"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {exporting ? 'EXPORTING...' : 'EXPORT PDF'}
          </Button>
          <Button
            data-testid="refresh-stock-button"
            onClick={fetchStock}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            REFRESH
          </Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="flex items-center gap-4">
          <Search className="w-4 h-4 text-text-muted" />
          <Input
            data-testid="stage-stock-search"
            type="text"
            placeholder="Search by Part ID or Part Name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md rounded-sm"
          />
          {search && (
            <span className="text-xs text-text-muted font-mono">
              {filtered.length} of {stock.length} entries
            </span>
          )}
        </div>
      </div>

      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-text-muted">Loading stock data...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            {search ? 'No matching parts found' : 'No stock data available'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part Name</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Received</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Issued</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Pending</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => (
                  <tr key={idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`stage-stock-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs">{item.part_id}</td>
                    <td className="p-4 align-middle">{item.part_name || '-'}</td>
                    <td className="p-4 align-middle">
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-slate-100 text-slate-700">
                        {item.stage}
                      </span>
                    </td>
                    <td className="p-4 align-middle text-right font-mono text-xs" data-testid={`received-${idx}`}>{(item.issued || 0).toLocaleString()}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs" data-testid={`issued-${idx}`}>{(item.received || 0).toLocaleString()}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs font-semibold">
                      <span className={item.qty < 0 ? 'text-red-600' : 'text-gray-900'}>
                        {item.qty.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
